import { useMutation, useQueryClient } from 'react-query';
import { useHistory, useLocation } from 'react-router';
import { wgApi } from '../../../app/src/features/api/wgApi';
import useLastCompletedConversation from '../../../parties/src/hooks/useLastCompletedConversation';
import { useAdvisoryReportsByParty } from '../features/overview/api/getAdvisoryReports';
import { AdvisoryReport } from '../types/AdvisoryReport';
import { getUseAdvisoryReportQKey } from './getAdvisoryReport';

interface Params {
  partyId: string;
  recommendationId?: string;
  conversationId?: string;
  assigned_to?: string;
  lang?: string;
}

const createAdvisoryReport = async ({
  partyId,
  recommendationId,
  conversationId,
  assigned_to,
  lang,
}: Params) => {
  const { data, isError } = await wgApi.post<AdvisoryReport>(
    `v1/api/advisory-reports`,
    {
      party_id: partyId,
      recommendation_id: recommendationId,
      conversation_id: conversationId,
      assigned_to: assigned_to,
    },
    {
      query: {
        lang,
      },
    },
  );
  if (isError) throw new Error();
  return data;
};

export const useCreateAdvisorReport = () => {
  const queryClient = useQueryClient();

  return useMutation(createAdvisoryReport, {
    onSuccess: (data: AdvisoryReport) => {
      queryClient.setQueryData(getUseAdvisoryReportQKey(data.id), data);
    },
  });
};

export const usePushNewAdvisoryReport = (
  partyId?: string,
): ((
  partyId?: string,
  recommendationId?: string,
  conversationId?: string,
  language?: string,
) => Promise<void>) => {
  const { pathname, search } = useLocation();
  const history = useHistory();
  const { mutateAsync: createAdvisoryReport } = useCreateAdvisorReport();
  const { data: lastConversation } = useLastCompletedConversation(partyId);
  const { data: items } = useAdvisoryReportsByParty();

  return async (
    partyId?: string,
    recommendationId?: string,
    conversationId?: string,
    language?: string,
  ) => {
    if (partyId) {
      if (lastConversation) {
        if (!recommendationId)
          recommendationId = lastConversation.recommendation?.id;
        if (!conversationId) conversationId = lastConversation.id;
      }

      const page = 'personalize';

      let advisoryReport;
      try {
        advisoryReport = await createAdvisoryReport({
          partyId,
          recommendationId,
          conversationId,
          lang: language,
        });
      } catch (e) {
        console.error(e);
      }

      const advisoryReportId = advisoryReport?.id || items?.[0].id;

      if (advisoryReportId)
        history.push({
          pathname: `/advisory-report/new/${page}/${advisoryReportId}`,
          search: `?party_id=${partyId}&backRef=${pathname + search}`,
        });
    }
  };
};
